import type { NextPage } from "next";
import { CopyableCodeBlock } from "../components/CopyableCodeBlock";
import { CopyableText } from "../components/CopyableText";
import { WithSidebar } from "../components/Sidebar";
import useUser from "../src/useUser";
import useTenant from "../src/useTenant";
import LoadingPage from "../components/LoadingPage";
import useEnv from "../src/useEnv";
import { EnvData, Tenant, Token } from "../src/types";
import useData from "../src/useData";
import { useRouter } from "next/router";
import Link from "next/link";

import WithIcon from "../components/WithIcon";
import { HiOutlineLightBulb, HiOutlineLightningBolt } from "react-icons/hi";
import Head from "next/head";
import { Tab, TabGroup } from "../components/Tab";
import { ReactNode, useState } from "react";
import { useSegmentCallback, useSegmentCallbackV2 } from "../src/segment";
import Listbox from "../components/Listbox";
import useSWR from "swr";
import useExampleAuthorize from "@/src/useExampleAuthorize";
import MainContent from "@/components/MainContent";

interface Instructions {
  language: "python" | "ruby" | "typescript" | "go" | "bash" | "csharp";
  installSteps: string;
  clientInitSteps: string | null;
  enforceSteps: string;
  docsLink: string;
}

interface AuthorizeParams {
  actorType: string;
  actorId: string;
  permission: string;
  resourceType: string;
  resourceId: string;
  // Description for `Verifying enforcement for your application` section
  enforcementContext: ReactNode;
}

interface FrameworkConfig {
  frameworks: string[];
  selectedFramework: string | null;
  onFrameworkChange: (framework: string) => void;
}

const productionOrEnforced = (tenant: Tenant, apiKeys?: Token[]) =>
  tenant.kind == "production" ||
  (apiKeys && apiKeys?.filter((k) => !!k.last_seen_at).length > 0);

const InstallInstructionsHeader: React.FC<{
  tenant: Tenant;
  selectedEnv: EnvData;
  frameworkConfig: FrameworkConfig;
  repoLink: string;
  noFramework?: boolean;
}> = ({ tenant, selectedEnv, frameworkConfig, repoLink, noFramework }) => {
  const segmentCallback = useSegmentCallback(
    "Install visit sample application"
  );
  const { data: apiKeys } = useData<Token[]>(() =>
    selectedEnv ? `/web/environment/${selectedEnv.pub_id}/tokens` : null
  );

  const showSampleApp = !productionOrEnforced(tenant, apiKeys);

  return (
    <>
      <div className="flex flex-row items-center space-x-2">
        <div className="font-bold">Select framework</div>
        <div className="w-48">
          <Listbox
            options={frameworkConfig.frameworks}
            onChange={(framework) => {
              // Guaranteed not to be an array but satisfying static type checker
              if (!Array.isArray(framework)) {
                frameworkConfig.onFrameworkChange(framework);
              }
            }}
            selectedValue={frameworkConfig.selectedFramework}
            className="text-base rounded border-2 border-[#352990] focus:border-[#352990] focus:outline-none focus:ring-1 focus:ring-[#352990] sm:text-sm"
          />
        </div>
      </div>
      {showSampleApp || noFramework ? (
        <div className={`mt-4 mb-4 p-2 rounded-md border border-yellow-300`}>
          <WithIcon icon={<HiOutlineLightningBolt />} top iconClassName="mt-1">
            <div className="text-base">
              {noFramework ? (
                <>
                  <span className="font-medium">
                    {`Don't`} use any of the listed web frameworks? You can
                    still use Oso Cloud!{" "}
                  </span>
                  <div>
                    Use the initialization code listed here, and place the
                    enforcement code before your business logic is executed --
                    for example, at the top of a request handler.
                  </div>
                </>
              ) : (
                <>
                  <span className="font-medium">
                    {`Don't`} have an application to integrate with?{" "}
                  </span>
                  <a
                    href={repoLink}
                    target="_blank"
                    rel="noreferrer"
                    className="underline text-blue-600"
                    onClick={() => segmentCallback()}
                  >
                    Clone our sample application
                  </a>
                  !
                </>
              )}
            </div>
          </WithIcon>
        </div>
      ) : (
        <div className="mb-4" />
      )}
    </>
  );
};

const SdkInstallInstructions: React.FC<{
  params: AuthorizeParams;
  instructions: Instructions;
}> = ({ params, instructions }) => {
  return (
    <>
      <div className="text-base font-bold self-end">
        Install the Oso Cloud package
      </div>
      <div className="mt-2" />
      <div>
        <CopyableText>{instructions.installSteps}</CopyableText>
      </div>
      <div className="mt-6" />

      <div className="text-base font-bold">
        Instantiate the Oso Cloud client
      </div>

      <div className="mt-1" />
      <WithIcon icon={<HiOutlineLightBulb />}>
        <div className="text-base">
          If you {`haven't`} created an API token and initialized your
          environment, do that first from the{" "}
          <a
            href="/settings/?tab=api-keys"
            target="_blank"
            rel="noreferrer"
            className="underline text-blue-600"
          >
            Settings
          </a>{" "}
          page.
        </div>
      </WithIcon>
      <div className="mt-2" />
      <CopyableCodeBlock
        code={instructions.clientInitSteps || ""}
        language={instructions.language}
      />

      <div className="mt-6" />

      <div className="text-base font-bold">
        Verify enforcement for your application
      </div>
      <div className="mt-1" />
      <div className="text-base">{params.enforcementContext}</div>
      <div className="mt-2" />
      <div>
        <CopyableCodeBlock
          code={instructions.enforceSteps}
          language={instructions.language}
        />
      </div>
      <div className="mt-6" />
      <div className="text-base">
        To explore the other available functions, jump to our{" "}
        <a
          href={instructions.docsLink}
          target="_blank"
          rel="noreferrer"
          className="underline text-blue-600"
        >
          client docs
        </a>
        .
      </div>
    </>
  );
};

const CliInstall: React.FC<{ params: AuthorizeParams }> = ({ params }) => {
  return (
    <>
      <div className="text-base font-bold">Install the Oso Cloud package</div>
      <div className="mt-2" />
      <div>
        <CopyableText>{`curl -L https://cloud.osohq.com/install.sh | bash`}</CopyableText>
      </div>
      <div className="mt-6" />
      <WithIcon icon={<HiOutlineLightBulb />}>
        <div className="text-base">
          If you {`haven't`} created an API token and initialized your
          environment, do that first from the{" "}
          <a
            href="/settings/?tab=api-keys"
            target="_blank"
            rel="noreferrer"
            className="underline text-blue-600"
          >
            Settings
          </a>{" "}
          page.
        </div>
      </WithIcon>
      <div className="mt-6" />
      <WithIcon icon={<HiOutlineLightBulb />}>
        <div className="text-base">
          You can install a specific version of the CLI {`(>=0.19.0)`} as
          follows.
        </div>
        <div>
          <CopyableText>{`curl -L https://cloud.osohq.com/install.sh | OSO_CLI_VERSION="x.y.z" bash`}</CopyableText>
        </div>
      </WithIcon>
      <div className="mt-6" />

      <div className="text-base font-bold">
        Verify enforcement for your application
      </div>
      <div className="mt-1" />
      <div className="text-base">{params.enforcementContext}</div>
      <div className="mt-2" />
      <div>
        <CopyableCodeBlock
          code={`oso-cloud authorize ${params.actorType}:${params.actorId} "${params.permission}" ${params.resourceType}:${params.resourceId}`}
          language="bash"
        />
      </div>
      <div className="mt-6" />
      <div className="text-base">
        To explore the other available functions, jump to our{" "}
        <a
          href="https://www.osohq.com/docs/app-integration/client-apis/cli"
          target="_blank"
          rel="noreferrer"
          className="underline text-blue-600"
        >
          client docs
        </a>
        .
      </div>
      <div className="mt-6" />
      <div className="text-base">
        For installation instructions on Windows, see the{" "}
        <a
          href="https://www.osohq.com/docs/develop/local-dev/env-setup#install-the-oso-cloud-cli"
          target="_blank"
          rel="noreferrer"
          className="underline text-blue-600"
        >
          Windows install guide
        </a>
        .
      </div>
    </>
  );
};

const OsoDevServerInstall: React.FC<{}> = () => {
  const track = useSegmentCallbackV2().bind(
    null,
    "Local Development Binary Download"
  );

  const devServerUrl = "https://oso-local-development-binary.s3.amazonaws.com";

  const { data: currentVersion } = useSWR(
    `${devServerUrl}/latest/version`,
    (url) => fetch(url).then((res) => res.text())
  );

  return (
    <>
      <div className="text-base font-bold">Install the Oso Dev Server</div>
      <div className="mt-2" />
      <ol className="list-decimal ml-4">
        <li>
          Download the latest build for your platform:
          <div className="mt-2 space-y-4">
            <div>
              <div className="font-medium mb-1">Linux (x86_64):</div>
              <CopyableText onCopy={() => track({ platform: "Linux x86_64" })}>
                {`curl -L -O ${devServerUrl}/latest/oso-local-development-binary-linux-x86_64.tar.gz`}
              </CopyableText>
            </div>
            <div>
              <div className="font-medium mb-1">Linux (arm64):</div>
              <CopyableText onCopy={() => track({ platform: "Linux arm64" })}>
                {`curl -L -O ${devServerUrl}/latest/oso-local-development-binary-linux-arm64.tar.gz`}
              </CopyableText>
            </div>
            <div>
              <div className="font-medium mb-1">macOS (x86_64):</div>
              <CopyableText onCopy={() => track({ platform: "macOS x86_64" })}>
                {`curl -L -O ${devServerUrl}/latest/oso-local-development-binary-macos-x86_64.tar.gz`}
              </CopyableText>
            </div>
            <div>
              <div className="font-medium mb-1">macOS (arm64):</div>
              <CopyableText onCopy={() => track({ platform: "macOS arm64" })}>
                {`curl -L -O ${devServerUrl}/latest/oso-local-development-binary-macos-arm64.tar.gz`}
              </CopyableText>
            </div>
          </div>
        </li>
        <li>
          Extract the downloaded archive:
          <div className="mt-1">
            <CopyableText>
              {`tar -xvzf oso-local-development-binary-*.tar.gz`}
            </CopyableText>
          </div>
          <div className="text-sm text-gray-600 mt-1">
            This will extract the <code>standalone</code> binary to your current
            directory.
          </div>
        </li>
        <li>
          Make the binary executable:
          <div className="mt-1">
            <CopyableText>{`chmod +x ./standalone`}</CopyableText>
          </div>
        </li>
      </ol>
      <div className="mt-6" />
      <div className="text-base font-bold">Run the Oso Dev Server</div>
      <div className="mt-1" />
      <div className="text-base">
        View setup and usage instructions in{" "}
        <a
          href="https://www.osohq.com/docs/development/oso-dev-server#setup"
          target="_blank"
          rel="noreferrer"
          className="underline text-blue-600"
        >
          the Oso Cloud docs
        </a>
        .
      </div>
      <div className="mt-6" />
      <div className="text-base font-bold">Versioning</div>
      <div className="mt-1" />
      <div className="text-base">
        The current version is <code>{currentVersion}</code>
        <br />
        <br />
        To install a specific version, replace `latest` in the download link
        with the version number, e.g.
        <pre>
          {`${devServerUrl}/${currentVersion}/oso-local-development-binary-linux-x86_64.tar.gz`}
        </pre>
      </div>
      <div className="mt-6" />
      <div className="text-base font-bold">Docker</div>
      <div className="mt-1" />
      <div className="text-base">
        We provide a{" "}
        <a
          href="https://gallery.ecr.aws/osohq/dev-server"
          target="_blank"
          rel="noreferrer"
          className="underline text-blue-600"
        >
          hosted Docker image
        </a>{" "}
        for running the Oso Dev Server in a containerized environment.
      </div>
    </>
  );
};

const NodeInstall: React.FC<{
  tenant: Tenant;
  selectedEnv: EnvData;
  params: AuthorizeParams;
}> = ({ tenant, selectedEnv, params }) => {
  const [framework, setFramework] = useState<string>("Express");

  const frameworks = ["Express", "None/Other"];

  const instructions: {
    [framework: string]: {
      enforceSteps: string;
    };
  } = {
    "None/Other": {
      enforceSteps: `const actor = {type: "${params.actorType}", id: "${params.actorId}"};
const resource = {type: "${params.resourceType}", id: "${params.resourceId}"};

if (await oso.authorize(actor, "${params.permission}", resource) === false) {
// Handle authorization failure
throw new Error("Action is not allowed");
}`,
    },
    Express: {
      enforceSteps: `// \`app\` is the Express instance
app.get(
"/test-oso",
async (req, res) => {
  const actor = {type: "${params.actorType}", id: "${params.actorId}"};
  const resource = {type: "${params.resourceType}", id: "${params.resourceId}"};

  if (await oso.authorize(actor, "${params.permission}", resource) === false) {
    // Handle authorization failure
    res.status(404).send("Not Found");
    return;
  }
  res.status(200).send("authorized by oso");
}
);`,
    },
  };

  const selectedInstructions = instructions[framework];

  return (
    <>
      <InstallInstructionsHeader
        tenant={tenant}
        selectedEnv={selectedEnv}
        frameworkConfig={{
          frameworks,
          selectedFramework: framework,
          onFrameworkChange: setFramework,
        }}
        repoLink="https://github.com/osohq/oso-by-example/tree/main/getting-started/node"
        noFramework={framework === "None/Other"}
      />
      <SdkInstallInstructions
        params={params}
        instructions={{
          language: "typescript",
          installSteps: `npm install oso-cloud`,
          clientInitSteps: `const { Oso } = require("oso-cloud");

const apiKey = "<please provide your api key here>";
const oso = new Oso("https://cloud.osohq.com", apiKey);`,
          docsLink: "https://www.osohq.com/docs/reference/client-apis/node",
          ...selectedInstructions,
        }}
      />
    </>
  );
};

const PythonInstall: React.FC<{
  tenant: Tenant;
  selectedEnv: EnvData;
  params: AuthorizeParams;
}> = ({ tenant, selectedEnv, params }) => {
  const [framework, setFramework] = useState<string>("Flask");

  const frameworks = ["Flask", "FastAPI", "None/Other"];

  const instructions: {
    [framework: string]: {
      enforceSteps: string;
    };
  } = {
    "None/Other": {
      enforceSteps: `actor = {"type": "${params.actorType}", "id": "${params.actorId}"}
resource = {"type": "${params.resourceType}", "id": "${params.resourceId}"}

if not oso.authorize(actor, "${params.permission}", resource):
  # Handle authorization failure
  raise Exception("Action is not allowed")`,
    },
    Flask: {
      enforceSteps: `@app.get("/test-oso")
def test_oso():
  actor = {"type": "${params.actorType}", "id": "${params.actorId}"}
  resource = {"type": "${params.resourceType}", "id": "${params.resourceId}"}

  if not oso.authorize(actor, "${params.permission}", resource):
    # Handle authorization failure
    abort(404)
  return "authorized by oso"`,
    },
    FastAPI: {
      enforceSteps: `@app.get("/test-oso")
def test_oso():
  actor = {"type": "${params.actorType}", "id": "${params.actorId}"}
  resource = {"type": "${params.resourceType}", "id": "${params.resourceId}"}

  if not oso.authorize(actor, "${params.permission}", resource):
    # Handle authorization failure
    raise HTTPException(status_code=404)
  return "authorized by oso"`,
    },
  };

  const selectedInstructions = instructions[framework];

  return (
    <>
      <InstallInstructionsHeader
        tenant={tenant}
        selectedEnv={selectedEnv}
        frameworkConfig={{
          frameworks,
          selectedFramework: framework,
          onFrameworkChange: setFramework,
        }}
        repoLink="https://github.com/osohq/oso-by-example/tree/main/getting-started/python"
        noFramework={framework === "None/Other"}
      />
      <SdkInstallInstructions
        params={params}
        instructions={{
          language: "python",
          installSteps: `pip install oso-cloud`,
          clientInitSteps: `from oso_cloud import Oso

api_key = "<please provide your api key here>"
oso = Oso(url="https://cloud.osohq.com", api_key=api_key)`,
          docsLink: "https://www.osohq.com/docs/reference/client-apis/python",
          ...selectedInstructions,
        }}
      />
    </>
  );
};

const RubyInstall: React.FC<{
  tenant: Tenant;
  selectedEnv: EnvData;
  params: AuthorizeParams;
}> = ({ tenant, selectedEnv, params }) => {
  const [framework, setFramework] = useState<string>("Sinatra");

  const frameworks = ["Sinatra", "None/Other"];

  const instructions: {
    [framework: string]: {
      enforceSteps: string;
    };
  } = {
    "None/Other": {
      enforceSteps: `actor = OsoCloud::Value.new(type: '${params.actorType}', id: '${params.actorId}')
resource = OsoCloud::Value.new(type: '${params.resourceType}', id: '${params.resourceId}')

if !oso.authorize(actor, '${params.permission}', resource)
  # Handle authorization failure
  raise Exception.new 'Action is not allowed'
end`,
    },
    Sinatra: {
      enforceSteps: `get '/test-oso' do
  actor = OsoCloud::Value.new(type: '${params.actorType}', id: '${params.actorId}')
  resource = OsoCloud::Value.new(type: '${params.resourceType}', id: '${params.resourceId}')

  if !oso.authorize(actor, '${params.permission}', resource)
    # Handle authorization failure
    halt 404, 'Not Found'
  end

  'authorized by oso'
end`,
    },
  };

  const selectedInstructions = instructions[framework];

  return (
    <>
      <InstallInstructionsHeader
        tenant={tenant}
        selectedEnv={selectedEnv}
        frameworkConfig={{
          frameworks,
          selectedFramework: framework,
          onFrameworkChange: setFramework,
        }}
        repoLink="https://github.com/osohq/oso-by-example/tree/main/getting-started/ruby"
        noFramework={framework === "None/Other"}
      />
      <SdkInstallInstructions
        params={params}
        instructions={{
          language: "ruby",
          installSteps: `gem install oso-cloud`,
          clientInitSteps: `require 'oso-cloud'

api_key = '<please provide your api key here>'
oso = OsoCloud::Oso.new(url: 'https://cloud.osohq.com', api_key:)`,
          docsLink: "https://www.osohq.com/docs/reference/client-apis/ruby",
          ...selectedInstructions,
        }}
      />
    </>
  );
};

const GoInstall: React.FC<{
  tenant: Tenant;
  selectedEnv: EnvData;
  params: AuthorizeParams;
}> = ({ tenant, selectedEnv, params }) => {
  const [framework, setFramework] = useState<string>("net/http");

  const frameworks = ["net/http", "None/Other"];

  const instructions: {
    [framework: string]: {
      enforceSteps: string;
    };
  } = {
    "None/Other": {
      enforceSteps: `actor := oso.Instance{Type: "${params.actorType}", ID: "${params.actorId}"}
resource := oso.Instance{Type: "${params.resourceType}", ID: "${params.resourceId}"}

allowed, e := osoClient.Authorize(actor, "${params.permission}", resource)
if e != nil || !allowed {
  // Handle authorization failure
  return nil, errors.new("Action is not allowed")
}`,
    },
    "net/http": {
      enforceSteps: `http.HandleFunc("/test-oso", func(w http.ResponseWriter, r *http.Request) {
  actor := oso.Instance{Type: "${params.actorType}", ID: "${params.actorId}"}
  resource := oso.Instance{Type: "${params.resourceType}", ID: "${params.resourceId}"}

  allowed, e := osoClient.Authorize(actor, "${params.permission}", resource)
  if e != nil || !allowed {
    // Handle authorization failure
    http.Error(w, "Not Found", http.StatusNotFound)
    return
  }

  fmt.Fprintf(w, "authorized by oso")
})`,
    },
  };

  const selectedInstructions = instructions[framework];

  return (
    <>
      <InstallInstructionsHeader
        tenant={tenant}
        selectedEnv={selectedEnv}
        frameworkConfig={{
          frameworks,
          selectedFramework: framework,
          onFrameworkChange: setFramework,
        }}
        repoLink="https://github.com/osohq/oso-by-example/tree/main/getting-started/go"
        noFramework={framework === "None/Other"}
      />
      <SdkInstallInstructions
        params={params}
        instructions={{
          language: "go",
          installSteps: `go get github.com/osohq/go-oso-cloud`,
          clientInitSteps: `import oso "github.com/osohq/go-oso-cloud"

apiKey := "<please provide your api key here>"
osoClient := oso.NewClient("https://cloud.osohq.com", apiKey)`,
          docsLink: "https://www.osohq.com/docs/reference/client-apis/go",
          ...selectedInstructions,
        }}
      />
    </>
  );
};

const DotNetInstall: React.FC<{
  tenant: Tenant;
  selectedEnv: EnvData;
  params: AuthorizeParams;
}> = ({ tenant, selectedEnv, params }) => {
  const [framework, setFramework] = useState<string>("ASP.NET Core Web App");

  const frameworks = ["ASP.NET Core Web App", "None/Other"];

  const instructions: {
    [framework: string]: {
      enforceSteps: string;
    };
  } = {
    "None/Other": {
      enforceSteps: `var actor = new OsoCloud.Value("${params.actorType}", "${params.actorId}");
var resource = new OsoCloud.Value("${params.resourceType}", "${params.resourceId}");

var allowed = await oso.Authorize(actor, "${params.permission}", resource);
if (!allowed) {
  // Handle authorization failure
  throw new Exception("Action is not allowed");
}`,
    },
    "ASP.NET Core Web App": {
      enforceSteps: `app.MapGet("/test-oso", async () => {
  var actor = new OsoCloud.Value("${params.actorType}", "${params.actorId}");
  var resource = new OsoCloud.Value("${params.resourceType}", "${params.resourceId}");

  bool allowed = await oso.Authorize(actor, "${params.permission}", resource);
  if (!allowed)
  {
    // Handle authorization failure
    return Results.NotFound("Not Found");
  }

  return Results.Ok("authorized by oso");
});`,
    },
  };

  const selectedInstructions = instructions[framework];

  return (
    <>
      <InstallInstructionsHeader
        tenant={tenant}
        selectedEnv={selectedEnv}
        frameworkConfig={{
          frameworks,
          selectedFramework: framework,
          onFrameworkChange: setFramework,
        }}
        repoLink="https://github.com/osohq/oso-by-example/tree/main/getting-started/dotnet"
        noFramework={framework === "None/Other"}
      />
      <SdkInstallInstructions
        params={params}
        instructions={{
          language: "csharp",
          installSteps: `dotnet add package OsoCloud`,
          clientInitSteps: `using OsoCloud;

string apiKey = "<please provide your api key here>";
var oso = new Oso("https://cloud.osohq.com", apiKey);`,
          docsLink: "https://www.osohq.com/docs/reference/client-apis/dotnet",
          ...selectedInstructions,
        }}
      />
    </>
  );
};

const Install: NextPage = () => {
  const { isLoading } = useUser();
  const { tenant } = useTenant();
  const { selectedEnv } = useEnv();
  const { query } = useRouter();
  const exampleAuthorize = useExampleAuthorize(selectedEnv);

  const tabs = [
    {
      tabKey: "typescript",
      title: "TypeScript",
    },
    {
      tabKey: "python",
      title: "Python",
    },
    {
      tabKey: "golang",
      title: "Go",
    },
    {
      tabKey: "ruby",
      title: "Ruby",
    },
    {
      tabKey: "dotnet",
      title: ".NET",
    },
    {
      tabKey: "cli",
      title: "CLI",
    },
    {
      tabKey: "local-binary",
      title: "Oso Dev Server",
    },
  ];
  const tabKeys = tabs.map((t) => t.tabKey);
  const selectedTabKey =
    query.tab && tabKeys.includes(query.tab.toString())
      ? query.tab.toString()
      : tabKeys[0];

  if (isLoading || !tenant || !selectedEnv) {
    return (
      <WithSidebar>
        <LoadingPage />
      </WithSidebar>
    );
  }

  const {
    actorType,
    actorId,
    permission,
    resourceType,
    resourceId,
    defaultSuggestion,
  } = exampleAuthorize;

  const enforcementContext = defaultSuggestion ? (
    <div>
      This sample code uses the actor and resource types from the GitCloud
      example in our documentation. After {`you've`} added a policy with
      resource(s) and permission(s), the sample code will be updated to reflect
      that.
    </div>
  ) : (
    <div>
      This sample code uses the <b>{actorType}</b> and <b>{resourceType}</b>{" "}
      types from your policy. The IDs are only meant as an illustration.
    </div>
  );

  const params = {
    actorId,
    actorType,
    permission,
    resourceId,
    resourceType,
    enforcementContext,
  };

  return (
    <>
      <Head>
        <title>Oso Cloud - Installation</title>
      </Head>
      <WithSidebar>
        <MainContent>
          <div className="font-space text-3xl">Install Oso</div>
          <div className="mt-2 mb-5 max-w-[1000px]">
            You can interact with Oso using a CLI or from within your
            application.
          </div>
          <TabGroup>
            <div className="flex flex-row">
              {tabs.map(({ tabKey, title }) => (
                <Link href={`?tab=${tabKey}`} passHref key={tabKey}>
                  <Tab title={title} isSelected={tabKey === selectedTabKey} />
                </Link>
              ))}
            </div>
          </TabGroup>

          <div className="mt-6 max-w-4xl">
            {
              {
                typescript: (
                  <NodeInstall
                    tenant={tenant}
                    selectedEnv={selectedEnv}
                    params={params}
                  />
                ),
                python: (
                  <PythonInstall
                    tenant={tenant}
                    selectedEnv={selectedEnv}
                    params={params}
                  />
                ),
                golang: (
                  <GoInstall
                    tenant={tenant}
                    selectedEnv={selectedEnv}
                    params={params}
                  />
                ),
                ruby: (
                  <RubyInstall
                    tenant={tenant}
                    selectedEnv={selectedEnv}
                    params={params}
                  />
                ),
                dotnet: (
                  <DotNetInstall
                    tenant={tenant}
                    selectedEnv={selectedEnv}
                    params={params}
                  />
                ),
                cli: <CliInstall params={params} />,
                "local-binary": <OsoDevServerInstall />,
              }[selectedTabKey]
            }
          </div>
        </MainContent>
      </WithSidebar>
    </>
  );
};

export default Install;
